/** 每日报告（屏规格 `docs/design/screens/reports.md`，#4598）。
 *
 * 字段对照 `someo_keel/report.py` 的真产出：索引 `GET /reports/daily`（含 `escalated_count`），
 * 正文 `GET /reports/daily/{date}`（`headline` 带 `first_seen_date` / `consecutive_days` / `escalated`，
 * 各策略分区 `pnl` / `positions` / `exposure` / `risk` / `actions` / `reconciliation`，`delivery`）。
 * 重建 `POST /reports/daily/{date}/build`、送达 `POST /reports/daily/{date}/send`，停机时照常可用。
 */

import { useState } from "react";

import { Card, Lede, Note, PageHead } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { money, signedMoney, tone } from "../../lib/format";
import { latestReport, shortUtc, sortHeadline, type DailyReportView, type StrategySection } from "../../lib/g1";
import { useAlertsLoose, useReportIndex, useReportView } from "../../lib/g1Queries";
import { ActionForm } from "../p2/Common";
import { Load } from "../overview/parts";

export function ReportsScreen(): React.ReactNode {
  const index = useReportIndex();
  const [picked, setPicked] = useState<string | null>(null);
  const latest = index.data ? latestReport(index.data) : null;
  const date = picked ?? latest?.trade_date ?? null;
  return (
    <>
      <PageHead title="每日报告" sub="GET /reports/daily" />
      <Lede>按日留档的日报：头部异常项（持续的排前）、各策略五项分区、送达结果；重建与送达停机时照常可用。</Lede>
      <div className="grid g32">
        <Card title="报告索引" nopad>
          <Load q={index} what="日报索引">
            {(rows) => (
              <Table
                cols={[{ label: "日期" }, { label: "状态" }, { label: "异常项", num: true }, { label: "持续升级", num: true }, { label: "送达" }]}
                rows={[...rows].sort((a, b) => b.trade_date.localeCompare(a.trade_date)).map((row) => ({
                  key: row.trade_date,
                  selected: row.trade_date === date,
                  onClick: () => setPicked(row.trade_date),
                  cls: row.status !== "ok" ? "warn" : "",
                  cells: [
                    row.trade_date,
                    <Pill tone={row.status === "ok" ? "ok" : "bad"}>{row.status}</Pill>,
                    row.headline_count,
                    row.escalated_count === null ? "—（未提供）" : row.escalated_count ? <span className="neg">{row.escalated_count}</span> : 0,
                    row.delivered ? <Pill tone="ok">已送达</Pill> : <Pill tone="neutral">未送达</Pill>,
                  ],
                }))}
                empty="还没有日报；纸上盘在每个交易日的次日 07:30 ET（北京 19:30）生成"
              />
            )}
          </Load>
        </Card>
        <div>{date ? <ReportDetail date={date} /> : null}</div>
      </div>
      {date ? (
        <div className="grid g2" style={{ marginTop: 12 }}>
          <ActionForm path={`/reports/daily/${date}/build`} title={`重建报告 · ${date}`} fields={[{ name: "operator", label: "operator" }, { name: "reason", label: "reason" }]} />
          <ActionForm path={`/reports/daily/${date}/send`} title={`送达飞书 · ${date}`} fields={[{ name: "operator", label: "operator" }, { name: "reason", label: "reason" }]} />
        </div>
      ) : null}
    </>
  );
}

function ReportDetail({ date }: { date: string }): React.ReactNode {
  const report = useReportView(date);
  const alerts = useAlertsLoose(date);
  return (
    <Load q={report} what={`${date} 日报`}>
      {(data) => (
        <>
          <Headline report={data} />
          <Delivery report={data} alerts={alerts.data ?? null} alertsError={alerts.isError} />
          <div className="grid g2" style={{ marginTop: 12 }}>
            {Object.entries(data.strategies).map(([sid, section]) => (
              <StrategyCard key={sid} sid={sid} section={section} />
            ))}
          </div>
        </>
      )}
    </Load>
  );
}

function Headline({ report }: { report: DailyReportView }): React.ReactNode {
  const items = sortHeadline(report.headline);
  return (
    <Card title={`异常项 · ${report.trade_date}`} sub={`status=${report.status} · 生成 ${shortUtc(report.generated_utc)}`} nopad>
      <Table
        cols={[{ label: "类别", width: "16%" }, { label: "策略", width: "22%" }, { label: "持续", width: "14%" }, { label: "首次出现", width: "14%" }, { label: "说明", wrap: true }]}
        rows={items.map((item, i) => ({
          key: `${item.kind}-${i}`,
          cls: item.escalated ? "bad" : "",
          cells: [
            item.kind,
            item.strategy_id || "—",
            item.consecutive_days === null ? "—（未提供）" : item.escalated ? <Pill tone="bad">已持续 {item.consecutive_days} 个交易日</Pill> : item.consecutive_days > 1 ? `已持续 ${item.consecutive_days} 个交易日` : "首次",
            item.first_seen_date ?? "—",
            item.detail,
          ],
        }))}
        empty={<span className="pos">当日无异常项（status={report.status}）</span>}
      />
    </Card>
  );
}

function Delivery({ report, alerts, alertsError }: { report: DailyReportView; alerts: { kind: string; summary: string; ts_utc: string }[] | null; alertsError: boolean }): React.ReactNode {
  const d = report.delivery;
  const related = (alerts ?? []).filter((a) => a.kind === "daily_report_failed" || a.kind === "feishu_delivery_failed");
  return (
    <Card title="送达" sub="daily.json delivery · GET /alerts?date=" cls={d && d.ok === false ? "fatal" : ""}>
      <dl className="kv">
        <dt>渠道</dt>
        <dd>{d?.channel ?? "—"}</dd>
        <dt>目标群</dt>
        <dd>{d?.target ?? "—（未提供）"}</dd>
        <dt>结果</dt>
        <dd>{!d ? <span className="faint">未送达</span> : d.ok ? <span className="pos">已送达</span> : <span className="neg">送达失败：{d.error ?? "—"}</span>}</dd>
        <dt>送达时间</dt>
        <dd>{shortUtc(d?.sent_utc)}</dd>
        <dt>相关告警</dt>
        <dd>{alertsError ? "未取到" : related.length ? related.map((a) => `${a.kind}：${a.summary}`).join("；") : "该日无报告相关告警"}</dd>
        <dt>触发留痕</dt>
        <dd>—（actions_log 未提供）</dd>
      </dl>
    </Card>
  );
}

function StrategyCard({ sid, section }: { sid: string; section: StrategySection }): React.ReactNode {
  const p = section.pnl;
  const actions = section.actions as { signals?: Record<string, number>; orders?: number; fills?: number };
  const risk = section.risk;
  return (
    <Card title={sid} sub={section.no_run ? "当天没有 run" : p.basis ?? undefined} cls={section.no_run ? "warn" : ""}>
      <dl className="kv">
        <dt>总盈亏（权益）</dt>
        <dd className={tone(p.total_pnl)}>{signedMoney(p.total_pnl)}</dd>
        <dt>已实现（平仓）</dt>
        <dd>{signedMoney(p.realized_closed)}</dd>
        <dt>未实现</dt>
        <dd>{signedMoney(p.unrealized)}</dd>
        <dt>现金变动（对照）</dt>
        <dd className="faint">{signedMoney(p.cash_change)}</dd>
        <dt>手续费 / 滑点</dt>
        <dd>
          {money(p.commissions)} / {money(p.slippage_cost)}
        </dd>
        <dt>持仓市值</dt>
        <dd>{money(p.position_value)}</dd>
        <dt>敞口 总 / 净</dt>
        <dd>
          {money(section.exposure.gross)} / {money(section.exposure.net)}
        </dd>
        <dt>当日动作</dt>
        <dd>
          受理 {actions.signals?.accepted ?? "—"} · 拒收 {actions.signals?.rejected ?? "—"} · 不下单 {actions.signals?.no_order ?? "—"} · 下单 {actions.orders ?? "—"} · 成交 {actions.fills ?? "—"}
        </dd>
        <dt>对账</dt>
        <dd className={section.reconciliation.balanced === false ? "neg" : ""}>
          {section.reconciliation.balanced === null ? "—" : section.reconciliation.balanced ? "平" : "不平"} {section.reconciliation.summary}
        </dd>
      </dl>
      {section.positions.length ? (
        <Table
          cols={[{ label: "标的" }, { label: "数量", num: true }, { label: "均价", num: true }, { label: "收盘", num: true }, { label: "市值", num: true }]}
          rows={section.positions.map((row) => ({ key: row.instrument_id, cells: [row.instrument_id, row.qty ?? "—", money(row.avg_px), money(row.mark), money(row.value)] }))}
        />
      ) : (
        <Note>无持仓</Note>
      )}
      <details>
        <summary>风险口径（原样）</summary>
        {risk === null ? <Note tone="warn">日报未给出该项</Note> : <pre className="code">{JSON.stringify(risk, null, 2).slice(0, 4000)}</pre>}
      </details>
    </Card>
  );
}
